import React, { useState, useEffect } from 'react';
import { ChevronDown, Globe, Menu, X, Sun, Moon, Monitor } from 'lucide-react';
import { navTabs, company } from '../mock';
import { useLang, LANGS } from '../i18n';
import { useTheme } from '../theme';

const THEME_OPTS = [['light', Sun], ['dark', Moon], ['system', Monitor]];

const Header = ({ activeTab, onTabChange, onLogoClick }) => {
  const { lang, setLang, t } = useLang();
  const { mode, setMode } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const currentLang = LANGS.find((l) => l.code === lang) || LANGS[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (id) => {
    onTabChange(id);
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 dark:bg-navy-deep/95 backdrop-blur-xl border-b border-navy/10 dark:border-white/10 shadow-sm dark:shadow-black/40'
          : 'bg-white/70 dark:bg-navy-deep/85 backdrop-blur-md dark:backdrop-blur-xl border-b border-white/20 dark:border-white/10'
      }`}
    >
      <div className="max-w-[1680px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-[84px]">
          {/* Logo — real anchor so it can be dragged to the browser address/bookmarks bar */}
          <a
            href={(typeof window !== 'undefined' ? window.location.origin : '') + '/'}
            onClick={(e) => { e.preventDefault(); if (onLogoClick) { onLogoClick(); } else { handleNav('introduction'); } }}
            data-testid="logo-button"
            className="flex items-center shrink-0"
            title="Intervantis Transport Solutions"
            aria-label="Intervantis home"
          >
            <img
              src="/brand/intervantis-horizontal-dark.png"
              alt="Intervantis - Logistics Powered by Engineering"
              className="h-[46px] md:h-[52px] w-auto object-contain dark:brightness-0 dark:invert"
              draggable="false"
            />
          </a>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-9">
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`nav-${tab.id}`}
                onClick={() => handleNav(tab.id)}
                className={`nav-underline text-[15px] font-body tracking-tight transition-colors ${
                  activeTab === tab.id ? 'is-active text-navy dark:text-white font-medium' : 'text-navy/70 dark:text-white/70 hover:text-navy dark:hover:text-white'
                }`}
              >
                {t(`nav.${tab.id}`)}
              </button>
            ))}
          </nav>

          {/* Right controls */}
          <div className="flex items-center gap-3 md:gap-4">
            <div data-testid="theme-selector" className="hidden sm:flex items-center rounded-full border border-navy/15 dark:border-white/20 p-0.5">
              {THEME_OPTS.map(([m, Icon]) => (
                <button
                  key={m}
                  data-testid={`theme-${m}`}
                  onClick={() => setMode(m)}
                  aria-label={`${m} mode`}
                  title={`${m.charAt(0).toUpperCase() + m.slice(1)} mode`}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                    mode === m ? 'bg-ember text-navy' : 'text-navy/55 dark:text-white/55 hover:text-navy dark:hover:text-white'
                  }`}
                >
                  <Icon size={15} strokeWidth={1.8} />
                </button>
              ))}
            </div>
            <div className="relative hidden sm:block">
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 text-navy/80 dark:text-white/80 hover:text-navy dark:hover:text-white border border-navy/15 dark:border-white/20 px-3 py-2 text-sm transition-colors"
              >
                <Globe size={15} strokeWidth={1.6} />
                <span className="font-body">{currentLang.short}</span>
                <ChevronDown size={14} strokeWidth={1.6} className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-navy border border-navy/10 dark:border-white/10 shadow-lg z-50">
                  {LANGS.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`block w-full text-left px-4 py-2.5 text-sm font-body hover:bg-navy/5 dark:hover:bg-white/5 ${lang === l.code ? 'text-ember font-medium' : 'text-navy/80 dark:text-white/80'}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              data-testid="request-consultation"
              onClick={() => handleNav('contact')}
              className="hidden sm:inline-flex items-center bg-navy dark:bg-ember text-white dark:text-navy text-sm font-body px-5 py-3 hover:bg-navy-deep dark:hover:bg-ember-hover transition-colors"
            >
              {t('header.request')}
            </button>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden text-navy dark:text-white p-1"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white dark:bg-navy border-t border-navy/10 dark:border-white/10 px-6 py-4">
          <nav className="flex flex-col">
            {navTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleNav(tab.id)}
                className={`text-left py-3 border-b border-navy/5 dark:border-white/5 font-body ${
                  activeTab === tab.id ? 'text-ember font-medium' : 'text-navy/80 dark:text-white/80'
                }`}
              >
                {t(`nav.${tab.id}`)}
              </button>
            ))}
            <button
              onClick={() => handleNav('contact')}
              className="mt-4 bg-navy dark:bg-ember text-white dark:text-navy py-3 font-body"
            >
              {t('header.request')}
            </button>
            <div className="mt-4 flex gap-2 flex-wrap">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-sm font-body border ${lang === l.code ? 'border-ember text-ember' : 'border-navy/15 dark:border-white/20 text-navy/70 dark:text-white/70'}`}
                >
                  {l.short}
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              {THEME_OPTS.map(([m, Icon]) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  aria-label={`${m} mode`}
                  className={`flex-1 py-2 flex items-center justify-center gap-1.5 text-sm font-body border rounded ${
                    mode === m ? 'border-ember text-ember' : 'border-navy/15 dark:border-white/20 text-navy/70 dark:text-white/70'
                  }`}
                >
                  <Icon size={14} strokeWidth={1.8} />
                  <span className="capitalize">{m}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
